
import {
    ContactPhoneOutlined as ContactPhoneOutlinedIcon, AddBoxOutlined as AddBoxOutlinedIcon,
    HistoryOutlined as HistoryOutlinedIcon,
    Delete as DeleteIcon, Email as EmailIcon,
    MultipleStop as MultipleStopIcon, VoiceOverOffRounded as VoiceOverOffRoundedIcon,
    SyncDisabled as SyncDisabledIcon,
    AdminPanelSettings as AdminPanelSettingsIcon,
} from "@mui/icons-material"

import { formatPhoneNumber, listScoutAccounts, getEnterprise, getUsersEmails, createAccount } from "../../services/scout"
import { isAccountBlocked } from "../../services/utils"
import React, { useEffect, useReducer, useState } from "react"
import lockedReducer from "../../lib/reducer"
import { Tooltip } from "@mui/material"
import Table from "../../components/ui/Table"
import AddNumber from "./modals/addnumber"
import { emitSnackbar } from "../../lib/snackbar-emitter"
import manageErrors from "../../lib/error-management"
import ConnectPhone from "./modals/connectphone"
import DeletePhone from "./modals/removephone"
import DisconnectPhone from "./modals/disconnectphone"
import { loader } from "../../lib/loader"
import ResendEmail from "./modals/resendemail"
import ConnectHistory from "./modals/connecthistory"
import ExclusionList from "./modals/exclusionlist"
import Supervisor from "./modals/supervisor"
import { renderExceptions, userHasGroup } from "../../services/utils"

import {
    getMaxLicenses
} from "../../services/utils"


const status_dict = {
    'open': (<span style={{ color: 'green' }}>Conectado</span>),
    'connecting': (<span style={{ color: 'orange' }}>Conectando</span>),
    'close': (<span style={{ color: 'red' }}>Desconectado</span>),
    'export': (<span style={{ color: 'blue' }}>Exportando</span>)
}

const possibleActions = ({ row, setState, blocked, enterprise }) => Object.entries({
    connect: { icon: <MultipleStopIcon />, title: 'Conectar', show: !blocked && !['open', 'export'].includes(row.status), hide: true, onClick: () => setState({ connect: row }) },
    disconnect: { icon: <SyncDisabledIcon />, title: 'Desconectar', show: !blocked && row.status === 'open', hide: true, onClick: () => setState({ disconnect: row }) },
    resendmail: { icon: <EmailIcon />, title: 'Reenviar e-mail', show: !blocked && !['open', 'export'].includes(row.status), onClick: () => setState({ resend: row }) },
    historic: { icon: <HistoryOutlinedIcon />, title: 'Histórico', show: !blocked && !['export'].includes(row.status), onClick: () => setState({ historic: row }) },
    excludedlist: { icon: <VoiceOverOffRoundedIcon />, title: 'Lista de exclusão', show: !blocked && renderExceptions(enterprise) && row.status === 'open', onClick: () => setState({ excluded: row }) },
    supervisor: { icon: <AdminPanelSettingsIcon />, title: "Alterar gestor", show: true && userHasGroup(["administrador"]), onClick: () => setState({ supervisor: row }) },
    delete: { icon: <DeleteIcon />, title: 'Excluir', show: !blocked && !['open', 'export'].includes(row.status) && userHasGroup(["administrador"]), onClick: () => setState({ delete: row }) }
})

const phoneManagementConfig = {}

const initialState = {
    rows: [],
    users: [],
    used_licenses: 0,
    total_licenses: 0,
    loading: false,
    create: null,
    connect: null,
    disconnect: null,
    resend: null,
    historic: null,
    excluded: null,
    delete: null,
    supervisor: null
}

const Phones = () => {

    const [enterprise, setEnterprise] = useState(null)
    const [state, setState] = useReducer(lockedReducer, initialState)

    const availableLicenses = state.total_licenses - state.used_licenses
    const isBlocked = isAccountBlocked(enterprise)

    const initialize = async () => {

        setState({ loading: true })
        loader.on()

        const [enterpriseData, users, scoutaccounts] = await Promise.all([getEnterprise(), getUsersEmails(), listScoutAccounts()])

        setEnterprise(enterpriseData)

        setState({
            ...initialState,
            total_licenses: getMaxLicenses(enterpriseData),
            used_licenses: scoutaccounts?.length || 0,
            users: users || [],
            rows: scoutaccounts.map(row => ({...row, status: row.connection_state === 'export' ? 'export' : row.status})) || [],
        })
        loader.off()
    }

    const subCreateAccount = async (data) => {
        setState({ loading: true })
        const response = await createAccount(data)
        if (response.status === 201) {
            emitSnackbar({ message: 'Conta criada com sucesso!', severity: 'success' })
            await initialize()
        } else {
            emitSnackbar({ message: manageErrors({ error: response.data }), severity: "warning" })
        }

        setState({ loading: false })
    }

    useEffect(() => {
        initialize()
    }, [])

    return (
        <>
            {!isBlocked && state.create &&
                <AddNumber users={state.users} data={state.create} handleClose={() => setState({ create: null })} submit={(subCreateAccount)} />
            }
            {!isBlocked && state.connect &&
                <ConnectPhone account={state.connect} handleClose={() => setState({ connect: null })} handleSuccess={initialize} />
            }
            {!isBlocked && state.disconnect &&
                <DisconnectPhone account={state.disconnect} handleClose={() => setState({ disconnect: null })} handleSuccess={initialize} />
            }
            {!isBlocked && state.resend &&
                <ResendEmail account={state.resend} handleClose={() => setState({ resend: null })} handleSuccess={initialize} />
            }
            {!isBlocked && state.delete &&
                <DeletePhone account={state.delete} handleClose={() => setState({ delete: null })} handleSuccess={initialize} />
            }
            {!isBlocked && state.historic &&
                <ConnectHistory account={state.historic} handleClose={() => setState({ historic: null })} />
            }
            {!isBlocked && state.excluded &&
                <ExclusionList account={state.excluded} handleClose={() => setState({ excluded: null })} handleSuccess={initialize} />
            }
            {!isBlocked && state.supervisor &&
                <Supervisor account={state.supervisor} users={state.users} handleClose={() => setState({ supervisor: null })} handleSuccess={initialize} />
            }
            <section className="scout-settings-section">
                <div className="scout-settings-section__label">
                    <ContactPhoneOutlinedIcon />
                    <span>{phoneManagementConfig?.sectionLabel || 'Números monitorados'}: {state.used_licenses} de {state.total_licenses} licenças utilizadas</span>
                </div>

                {!state.loading && (
                    <>
                        <Table
                            columns={[
                                { id: 'name', label: 'Nome', render: (row) => row.name },
                                { id: 'phone', label: 'Telefone', render: (row) => formatPhoneNumber(row.phone_number) },
                                { id: 'status', label: 'Status', render: (row) => status_dict[row.status] },
                                { id: "create_by_name", label: "Gestor", render: (row) => row.created_by_name },
                                {
                                    id: 'actions',
                                    label: '',
                                    align: 'right',
                                    render: (row) => (
                                        <div className="scout-settings-table__actions">
                                            {possibleActions({ row, setState, blocked: isBlocked, enterprise: enterprise }).map(([action, data]) => (
                                                <React.Fragment key={`${row.id}-${action}`}>
                                                    {!(!data.show && data.hide) && (
                                                        <Tooltip title={data.show ? data.title : "Indisponível"}>
                                                            <span>
                                                                <button
                                                                    onClick={data.onClick}
                                                                    disabled={!data.show}
                                                                    type="button"
                                                                    className={`scout-settings-icon-action ${data.show ? '' : 'disabled'}`}>
                                                                    {data.icon}
                                                                </button>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    ),
                                },
                            ]}
                            data={state.rows}
                            rowKey={(row) => row.id}
                            emptyMessage="Nenhum número monitorado encontrado"
                            className="scout-settings-card--table"
                            tableClassName="scout-settings-table--phones"
                        />

                        {userHasGroup(["administrador"]) &&
                            <button
                                onClick={() => !isBlocked && setState({ create: { id: 0 } })}
                                type="button"
                                className="scout-settings-add-row"
                                disabled={isBlocked}
                            >
                                <AddBoxOutlinedIcon />
                                <span>{phoneManagementConfig?.addLabel || 'Adicionar'} <span style={{ fontWeight: 'lighter' }}>({availableLicenses} disponíveis)</span></span>
                            </button>
                        }
                    </>
                )}

                {state.loading ? <p className="scout-inline-feedback">Atualizando telefones...</p> : null}
                {/* {accountsError ? <p className="scout-inline-feedback">{accountsError}</p> : null} */}
            </section>
        </>
    )
}

export default Phones